import type { Hlc } from '../types';

// Hybrid Logical Clock: "<wallMs 15 cyfr>-<licznik 5 cyfr>-<węzeł>".
// Stała szerokość pól sprawia, że porównanie tekstowe = porównanie czasu.
const WALL_DIGITS = 15;
const COUNTER_DIGITS = 5;
const MAX_COUNTER = 10 ** COUNTER_DIGITS - 1;

export interface HlcState {
  wallMs: number;
  counter: number;
  node: string;
}

export interface HlcParts {
  wallMs: number;
  counter: number;
  node: string;
}

export function hlcInit(node: string): HlcState {
  return { wallMs: 0, counter: 0, node };
}

export function hlcFormat(wallMs: number, counter: number, node: string): Hlc {
  return `${String(wallMs).padStart(WALL_DIGITS, '0')}-${String(counter).padStart(COUNTER_DIGITS, '0')}-${node}`;
}

export function hlcParse(hlc: Hlc): HlcParts {
  const a = hlc.indexOf('-');
  const b = hlc.indexOf('-', a + 1);
  if (a !== WALL_DIGITS || b !== WALL_DIGITS + COUNTER_DIGITS + 1) {
    throw new Error(`Nieprawidłowy HLC: ${hlc}`);
  }
  return {
    wallMs: Number(hlc.slice(0, a)),
    counter: Number(hlc.slice(a + 1, b)),
    node: hlc.slice(b + 1),
  };
}

export function isHlc(value: unknown): value is Hlc {
  if (typeof value !== 'string') return false;
  try {
    hlcParse(value);
    return true;
  } catch {
    return false;
  }
}

export function compareHlc(a: Hlc, b: Hlc): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function bump(wallMs: number, counter: number, node: string): HlcState {
  if (counter > MAX_COUNTER) return { wallMs: wallMs + 1, counter: 0, node };
  return { wallMs, counter, node };
}

/** Nowy znacznik dla zdarzenia lokalnego. */
export function hlcTick(state: HlcState, nowMs: number): [Hlc, HlcState] {
  const next =
    nowMs > state.wallMs
      ? { wallMs: nowMs, counter: 0, node: state.node }
      : bump(state.wallMs, state.counter + 1, state.node);
  return [hlcFormat(next.wallMs, next.counter, next.node), next];
}

/** Uwzględnia znacznik z innego urządzenia, żeby kolejne lokalne były od niego późniejsze. */
export function hlcReceive(state: HlcState, remote: Hlc, nowMs: number): HlcState {
  const r = hlcParse(remote);
  const wall = Math.max(state.wallMs, r.wallMs, nowMs);
  let counter: number;
  if (wall === state.wallMs && wall === r.wallMs) counter = Math.max(state.counter, r.counter) + 1;
  else if (wall === state.wallMs) counter = state.counter + 1;
  else if (wall === r.wallMs) counter = r.counter + 1;
  else counter = 0;
  return bump(wall, counter, state.node);
}
